import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import AgencyAnalytics from '@/pages/AgencyAnalytics'
import DashboardLayout from '@/components/DashboardLayout'
import GlassCard from '@/components/ui/GlassCard'
import api from '@/services/api'
import {
  Users, MessageSquare, Star, TrendingUp,
  Home, Eye, Link2, MessageCircle, Loader2,
  Calendar
} from 'lucide-react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'

interface AnalyticsData {
  totalLeads: number
  averageScore: number
  totalConversations: number
  totalProperties: number
  uniqueSessions: number
  uniqueVisitors: number
  chatsInitiated: number
  conversionRate: string
}

interface ChartData {
  date: string
  count: number
}

// Componente wrapper que decide qué dashboard mostrar
const Analytics: React.FC = () => {
  const { agent, isLoading } = useAuth()

  // Mientras carga auth, mostrar loading
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
          <span className="ml-3 text-white/70">Cargando...</span>
        </div>
      </DashboardLayout>
    )
  }

  // Si es agencia, mostrar AgencyAnalytics
  if (agent?.sellers_count && agent.sellers_count > 0) {
    return <AgencyAnalytics />
  }

  // Si es agente individual, mostrar IndividualAnalytics
  return <IndividualAnalytics />
}

// Componente para agentes individuales
const IndividualAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [leadsChart, setLeadsChart] = useState<ChartData[]>([])
  const [conversationsChart, setConversationsChart] = useState<ChartData[]>([])
  const [timeRange, setTimeRange] = useState('30')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAllData()
  }, [timeRange])

  const fetchAllData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [analyticsRes, leadsRes, conversationsRes] = await Promise.all([
        api.get(`/api/agents/analytics?timeRange=${timeRange}`),
        api.get(`/api/agents/analytics/leads-by-day?days=${timeRange}`),
        api.get(`/api/agents/analytics/conversations-by-day?days=${timeRange}`)
      ])

      setAnalytics(analyticsRes.data)

      const formatChartData = (data: any[]) => {
        return data.map(item => ({
          date: new Date(item.date).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short'
          }),
          count: parseInt(item.count)
        }))
      }

      setLeadsChart(formatChartData(leadsRes.data.data || []))
      setConversationsChart(formatChartData(conversationsRes.data.data || []))
    } catch (err) {
      console.error('Error fetching analytics:', err)
      setError('Error al cargar analytics')
    } finally {
      setLoading(false)
    }
  }

  const metrics = analytics ? [
    {
      label: 'Total Leads',
      value: analytics.totalLeads,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      bg: 'bg-blue-500/20'
    },
    {
      label: 'Conversaciones',
      value: analytics.totalConversations,
      icon: MessageSquare,
      color: 'from-purple-500 to-purple-600',
      bg: 'bg-purple-500/20'
    },
    {
      label: 'Score Promedio',
      value: analytics.averageScore.toFixed(1),
      icon: Star,
      color: 'from-yellow-500 to-yellow-600',
      bg: 'bg-yellow-500/20'
    },
    {
      label: 'Tasa Conversión',
      value: `${analytics.conversionRate}%`,
      icon: TrendingUp,
      color: 'from-green-500 to-green-600',
      bg: 'bg-green-500/20'
    },
    {
      label: 'Propiedades',
      value: analytics.totalProperties,
      icon: Home,
      color: 'from-indigo-500 to-indigo-600',
      bg: 'bg-indigo-500/20'
    },
    {
      label: 'Visitantes Únicos',
      value: analytics.uniqueVisitors,
      icon: Eye,
      color: 'from-pink-500 to-pink-600',
      bg: 'bg-pink-500/20'
    },
    {
      label: 'Sesiones',
      value: analytics.uniqueSessions,
      icon: Link2,
      color: 'from-cyan-500 to-cyan-600',
      bg: 'bg-cyan-500/20'
    },
    {
      label: 'Chats Iniciados',
      value: analytics.chatsInitiated,
      icon: MessageCircle,
      color: 'from-orange-500 to-orange-600',
      bg: 'bg-orange-500/20'
    }
  ] : []

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">📊 Analytics</h1>
            <p className="text-gray-300">Métricas y análisis de tu actividad</p>
          </div>

          {/* Selector de período */}
          <div className="flex items-center gap-2 bg-white/10 rounded-lg p-1">
            <Calendar className="h-4 w-4 text-white/60 ml-2" />
            {['7', '30', '90'].map((days) => (
              <button
                key={days}
                onClick={() => setTimeRange(days)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  timeRange === days
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                {days}d
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-white/70">Cargando analytics...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <GlassCard className="p-6 border-red-500/30">
            <p className="text-red-400">{error}</p>
          </GlassCard>
        )}

        {/* Metrics Grid */}
        {!loading && analytics && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {metrics.map((metric, index) => (
                <GlassCard key={index} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{metric.label}</p>
                      <p className="text-2xl font-bold text-white">{metric.value}</p>
                    </div>
                    <div className={`p-2 rounded-lg ${metric.bg}`}>
                      <metric.icon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Leads Chart */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-400" />
                  Leads por Día
                </h3>
                {leadsChart.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={leadsChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                      <XAxis dataKey="date" stroke="#ffffff60" fontSize={12} />
                      <YAxis stroke="#ffffff60" fontSize={12} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: '1px solid #ffffff20',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#fff' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#3b82f6"
                        strokeWidth={3}
                        dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                        name="Leads"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-white/40">
                    No hay datos para el período seleccionado
                  </div>
                )}
              </GlassCard>

              {/* Conversations Chart */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-purple-400" />
                  Conversaciones por Día
                </h3>
                {conversationsChart.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={conversationsChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                      <XAxis dataKey="date" stroke="#ffffff60" fontSize={12} />
                      <YAxis stroke="#ffffff60" fontSize={12} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: '1px solid #ffffff20',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#fff' }}
                      />
                      <Bar
                        dataKey="count"
                        fill="#a855f7"
                        radius={[4, 4, 0, 0]}
                        name="Conversaciones"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-white/40">
                    No hay datos para el período seleccionado
                  </div>
                )}
              </GlassCard>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  )
}

export default Analytics
